import { Card } from 'antd'
import Button from 'components/Button'
import LoaderModal from 'components/Modals/LoaderModal'
import MessageModal from 'components/Modals/MessageModal'
import ReinvestWrapper from 'components/Wrapper/ReinvestWrapper'
import useFormProvider from 'hooks/useFormContext'
import { useRiseUpAPIV1 } from 'hooks/useRiseUpAPIV1'
import { IFormProps } from 'interfaces'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { consoleLog } from 'utils/simpleLogger'
import Side1 from '../../../assets/new_side_1.jpg'

const MAX_ATTEMPTS = 3

const Verify = (props: IFormProps) => {
  const [ssnIndexes, setSsnIndexes] = useState<number[]>([])
  const [error, setError] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [errorModal, setErrorModal] = useState(false)
  const [attempts, setAttempts] = useState(MAX_ATTEMPTS)

  const navigate = useNavigate()
  const watchAllFields = props.watch()
  const { email } = useFormProvider()
  const { verifyReturningInvestor } = useRiseUpAPIV1()

  useEffect(() => {
    if (email !== '') {
      props.setValue('email', email)
    } else {
      navigate('/registration')
    }
  }, [email])

  useEffect(() => {
    const indexes = getRandomIndexes()
    setSsnIndexes(indexes)
  }, [])

  useEffect(() => {
    const allFieldsFilled = ssnIndexes.every((index) => watchAllFields[index] && /^[0-9]$/.test(watchAllFields[index]))
    const yearOfBirthFilled = !!watchAllFields.yearOfBirth

    setIsFormValid(allFieldsFilled && yearOfBirthFilled)
  }, [watchAllFields])

  const getRandomIndexes = (): number[] => {
    const indexes: number[] = []
    while (indexes.length < 4) {
      const randomIndex = Math.floor(Math.random() * 9) + 1
      if (!indexes.includes(randomIndex)) {
        indexes.push(randomIndex)
      }
    }
    return indexes.sort((a, b) => a - b)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    const formData = props.watch()
    const ssnDigits = ssnIndexes.map((index) => ({
      position: index,
      value: parseInt(formData[index.toString()], 10),
    }))

    if (!formData.yearOfBirth) {
      setError('Please enter your year of birth.')
      return
    }

    try {
      const res = await verifyReturningInvestor({
        ssnDigits,
        birthYear: Number(formData.yearOfBirth),
        email,
      })
      if (res.data.statusCode === 200) {
        props.setValue('onboardingId', res?.data?.data?.data?.onboardingId)
        setShowModal(true)
        setIsLoading(false)
      } else {
        setIsLoading(false)
        const newAttempts = attempts - 1
        setAttempts(newAttempts)

        if (newAttempts <= 0) {
          props.reset()
          setErrorModal(true)
        }
        setError(res.data.message)
      }
    } catch (error) {
      consoleLog('Verifying returning investor error:', error)
      setIsLoading(false)
    }
  }

  const handleInputChange = (index: number, value: string) => {
    if (/^[0-9]?$/.test(value)) {
      props.setValue(index.toString(), value)
      props.clearErrors(index.toString())
      setError('')
    }
  }

  return (
    <ReinvestWrapper sideImage={Side1} title="Verify Your Identity" currentStage={Number(props.currentStep)}>
      <Card className="w-full max-w-md">
        <Card.Meta
          description={
            <p className="mb-4 text-sm text-gray-600">
              Please enter the following digits of your Social Security Number and your year of birth:
            </p>
          }
        />
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col">
            <label>Email:</label>
            <div className="my-1 block w-full rounded-md border-gray-300 font-semibold shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
              {email}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {ssnIndexes.map((index) => (
              <div key={index}>
                <label htmlFor={`ssn${index}`} className="block text-sm font-medium text-gray-700">
                  SSN Digit {index}
                </label>
                <input
                  {...props.registerField(index.toString(), {
                    required: true,
                    pattern: /^[0-9]$/,
                    maxLength: 1,
                  })}
                  type="text"
                  inputMode="numeric"
                  id={`ssn${index}`}
                  maxLength={1}
                  className="my-2 h-[42px] w-full rounded border border-gray-300 bg-gray-50 px-3 text-sm leading-5 text-gray-700 focus:text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-500 md:mb-0 md:mt-1 md:rounded md:py-2 md:text-sm"
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault()
                    }
                  }}
                />
                {props.errors[index] && <p className="mt-1 text-xs text-red-500">Please enter a single digit (0-9).</p>}
              </div>
            ))}
          </div>
          <div>
            <label htmlFor="yearOfBirth" className="block text-sm font-medium text-gray-700">
              Year of Birth
            </label>
            <input
              {...props.registerField('yearOfBirth', {
                required: true,
                pattern: /^\d{4}$/,
                validate: (value: any) => {
                  const year = parseInt(value)
                  const currentYear = new Date().getFullYear()
                  return year >= 1900 && year <= currentYear
                },
              })}
              type="number"
              id="yearOfBirth"
              placeholder="YYYY"
              className="my-2 h-[42px] w-full rounded border border-gray-300 bg-gray-50 px-3 text-sm leading-5 text-gray-700 focus:text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-500 md:mb-0 md:mt-1 md:rounded md:py-2 md:text-sm"
              onChange={(e) => {
                props.setValue('yearOfBirth', e.target.value)
                props.clearErrors('yearOfBirth')
                setError('')
              }}
            />
            {props.errors.yearOfBirth && (
              <p className="mt-1 text-xs text-red-500">Please enter a valid year of birth (1900-present).</p>
            )}
          </div>
          {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
          <Button btnName={'Verify'} btnType={'button'} disabled={!isFormValid} onClickHandler={handleSubmit} />
        </form>
        <LoaderModal isVisible={isLoading} />
        <MessageModal
          isVisible={showModal}
          onProceed={() => {
            props.setCurrentStage(2)
            props.nextStep('2')
          }}
          onCancel={() => {
            setShowModal(false)
          }}
          title={'You have successfully verified your identity'}
          description={'You can now proceed with reinvesting.'}
          type={'success'}
          btn2={'Ok'}
        />
        <MessageModal
          isVisible={errorModal}
          onProceed={() => {
            navigate('/registration')
          }}
          onCancel={() => {
            navigate('/registration')
          }}
          title={'You have failed to verify your identity'}
          description={'You can invest again using a different email.'}
          type={'error'}
          btn2={'Ok'}
        />
      </Card>
      <div className="mt-4 text-base leading-5 text-gray-500">
        In order to proceed with reinvesting, let's verify your details first
      </div>
    </ReinvestWrapper>
  )
}

export default Verify
